import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark, faCrown, faEye, faHeart, faLock } from "@fortawesome/free-solid-svg-icons";
import type { Artwork } from "@/data/mockArtworks";
import { useArtworkInteractions } from "@/hooks/useArtworkInteractions";

interface ArtworkHoverOverlayProps {
  artwork: Artwork;
  onVisualize: (artwork: Artwork) => void;
  className?: string;
}

export function ArtworkHoverOverlay({ artwork, onVisualize, className }: ArtworkHoverOverlayProps) {
  const { isLiked, isSaved, toggleLike, toggleSave, loading } = useArtworkInteractions(artwork.id);

  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-transparent via-[#14022a]/45 to-[#05000f] opacity-85 transition-opacity duration-500 md:opacity-0 md:group-hover:opacity-95" />
      <div
        className={clsx(
          "absolute inset-0 z-10 flex h-full flex-col p-4 pointer-events-auto opacity-100 transition-all duration-300 ease-out sm:p-5 md:pointer-events-none md:translate-y-6 md:opacity-0 md:group-hover:pointer-events-auto md:group-hover:translate-y-0 md:group-hover:opacity-100",
          className
        )}
      >

        {/* Grupo de botões centralizado no mobile */}
        <div className="absolute inset-x-0 bottom-6 flex items-center justify-center gap-2 md:hidden">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleLike();
            }}
            disabled={loading}
            aria-label={isLiked ? "Remover curtida" : "Curtir arte"}
            className={clsx(
              "inline-flex h-7 w-7 items-center justify-center rounded-full border backdrop-blur-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60",
              isLiked
                ? "border-red-500/40 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                : "border-white/20 bg-white/10 text-white/60 hover:bg-white/20 hover:text-white",
              loading && "opacity-50 cursor-not-allowed"
            )}
          >
            <FontAwesomeIcon icon={faHeart} className={clsx("h-3 w-3", isLiked && "animate-pulse")} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleSave();
            }}
            disabled={loading}
            aria-label={isSaved ? "Remover dos salvos" : "Salvar para favoritos"}
            className={clsx(
              "inline-flex h-7 w-7 items-center justify-center rounded-full border backdrop-blur-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60",
              isSaved
                ? "border-brand-secondary/40 bg-brand-secondary/20 text-brand-secondary hover:bg-brand-secondary/30"
                : "border-white/20 bg-white/10 text-white/60 hover:bg-white/20 hover:text-white",
              loading && "opacity-50 cursor-not-allowed"
            )}
          >
            <FontAwesomeIcon icon={faBookmark} className={clsx("h-3 w-3", isSaved && "animate-pulse")} />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onVisualize(artwork);
            }}
            aria-label="Visualizar arte"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-secondary/30 text-white backdrop-blur-md transition hover:bg-brand-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60"
          >
            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
          </button>
        </div>
        {/* Grupo de botões original para desktop */}
        <div className="relative mt-auto hidden md:flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleLike();
            }}
            disabled={loading}
            aria-label={isLiked ? "Remover curtida" : "Curtir arte"}
            className={clsx(
              "inline-flex h-7 w-7 items-center justify-center rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60",
              isLiked
                ? "border-red-500/50 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                : "border-white/20 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white",
              loading && "opacity-50 cursor-not-allowed"
            )}
          >
            <FontAwesomeIcon icon={faHeart} className={clsx("h-3 w-3", isLiked && "animate-pulse")} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleSave();
            }}
            disabled={loading}
            aria-label={isSaved ? "Remover dos salvos" : "Salvar para favoritos"}
            className={clsx(
              "inline-flex h-7 w-7 items-center justify-center rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60",
              isSaved
                ? "border-brand-secondary/50 bg-brand-secondary/20 text-brand-secondary hover:bg-brand-secondary/30"
                : "border-white/20 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white",
              loading && "opacity-50 cursor-not-allowed"
            )}
          >
            <FontAwesomeIcon icon={faBookmark} className={clsx("h-3 w-3", isSaved && "animate-pulse")} />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onVisualize(artwork);
            }}
            aria-label="Visualizar arte"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-secondary/30 text-white transition hover:bg-brand-secondary/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/60"
          >
            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
          </button>
        </div>
      </div>
    </>
  );
}
